import { Button } from "@package/subframe/components/Button";
import {
  FeatherClock,
  FeatherKey,
  FeatherLock,
  FeatherShield,
  FeatherUserCheck,
  FeatherUserX,
} from "@subframe/core";
import { Link } from "@tanstack/react-router";
import { useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { ChangeAccessRoleDialog } from "@/features/account-actions/ChangeAccessRoleDialog";
import { ChangeAccountStatusDialog } from "@/features/account-actions/ChangeAccountStatusDialog";
import { ResetPasswordDialog } from "@/features/account-actions/ResetPasswordDialog";
import { SetPinDialog } from "@/features/account-actions/SetPinDialog";
import type { EmployeeLoginAndSecurityDto } from "@/generated/model/employeeLoginAndSecurityDto";
import type { NurseryRole } from "@/generated/model/nurseryRole";
import { accountActionsFor, assignableRoles } from "@/lib/account-actions";
import { accountStatusLabel, formatProfileTimestamp } from "@/lib/people";
import { nurseryRoleLabel } from "@/lib/roles";
import { staffUsername } from "@/lib/staff-username";

import { ProfileSectionCard } from "../ProfileSectionCard";

type OpenDialog = "status" | "password" | "pin" | "role" | null;

interface LoginAndSecuritySectionProps {
  nurseryId: string;
  userId: string;
  displayName: string;
  email: string;
  nurseryRole: NurseryRole;
  loginAndSecurity: EmployeeLoginAndSecurityDto;
  canManageLogin: boolean;
  pinLoginEnabled: boolean;
}

/** Last login and account created are shown, never edited. Actions follow accountActionsFor(). */
export function LoginAndSecuritySection({
  nurseryId,
  userId,
  displayName,
  email,
  nurseryRole,
  loginAndSecurity,
  canManageLogin,
  pinLoginEnabled,
}: LoginAndSecuritySectionProps) {
  const { isPlatformAdmin, userRoleType } = useAuth();
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const username = staffUsername(email);
  const mayAssignManager = isPlatformAdmin || userRoleType === "ORG_ADMIN";
  const actions = accountActionsFor({
    canManageLogin,
    accountStatus: loginAndSecurity.accountStatus,
    pinLoginEnabled,
    targetRole: nurseryRole,
    mayAssignManager,
  });
  const hasActions =
    actions.statusToggle !== null ||
    actions.resetPassword ||
    actions.resetPin ||
    actions.changeRole ||
    actions.viewHistory;

  const closeDialog = (open: boolean) => {
    if (!open) setOpenDialog(null);
  };

  return (
    <>
      <ProfileSectionCard
        title="Login and security"
        testId="profile-section-login-and-security"
        fields={[
          { label: "Username", value: username },
          {
            label: "Account status",
            value: accountStatusLabel(loginAndSecurity.accountStatus),
          },
          {
            label: "Last login",
            value: formatProfileTimestamp(loginAndSecurity.lastLoginAt),
          },
          {
            label: "Account created",
            value: formatProfileTimestamp(loginAndSecurity.accountCreatedAt),
          },
          { label: "Access role", value: nurseryRoleLabel(nurseryRole) },
        ]}
      >
        {hasActions && (
          <div className="flex w-full flex-wrap items-center gap-2 border-t border-solid border-neutral-200 pt-3">
            {actions.statusToggle === "deactivate" && (
              <Button
                variant="destructive-secondary"
                icon={<FeatherUserX />}
                onClick={() => setOpenDialog("status")}
              >
                Deactivate account
              </Button>
            )}
            {actions.statusToggle === "activate" && (
              <Button
                variant="neutral-secondary"
                icon={<FeatherUserCheck />}
                onClick={() => setOpenDialog("status")}
              >
                Reactivate account
              </Button>
            )}
            {actions.resetPassword && (
              <Button
                variant="neutral-secondary"
                icon={<FeatherLock />}
                onClick={() => setOpenDialog("password")}
              >
                Reset password
              </Button>
            )}
            {actions.resetPin && (
              <Button
                variant="neutral-secondary"
                icon={<FeatherKey />}
                onClick={() => setOpenDialog("pin")}
              >
                Reset PIN
              </Button>
            )}
            {actions.changeRole && (
              <Button
                variant="neutral-secondary"
                icon={<FeatherShield />}
                onClick={() => setOpenDialog("role")}
              >
                Change access role
              </Button>
            )}
            {actions.viewHistory && (
              <Link
                to="/people/$userId/login-history"
                params={{ userId }}
                className="inline-flex items-center gap-1 text-body-bold font-body-bold text-primary-navy-700 hover:underline"
              >
                <FeatherClock />
                View login history
              </Link>
            )}
          </div>
        )}
      </ProfileSectionCard>

      {actions.statusToggle && (
        <ChangeAccountStatusDialog
          userId={userId}
          displayName={displayName}
          action={actions.statusToggle}
          open={openDialog === "status"}
          onOpenChange={closeDialog}
        />
      )}
      {actions.resetPassword && (
        <ResetPasswordDialog
          userId={userId}
          displayName={displayName}
          username={username}
          open={openDialog === "password"}
          onOpenChange={closeDialog}
        />
      )}
      {actions.resetPin && (
        <SetPinDialog
          userId={userId}
          displayName={displayName}
          open={openDialog === "pin"}
          onOpenChange={closeDialog}
        />
      )}
      {actions.changeRole && (
        <ChangeAccessRoleDialog
          // Remounts on a role change made elsewhere, so it never saves a stale pick.
          key={nurseryRole}
          nurseryId={nurseryId}
          userId={userId}
          displayName={displayName}
          currentRole={nurseryRole}
          roles={assignableRoles({ targetRole: nurseryRole, mayAssignManager })}
          open={openDialog === "role"}
          onOpenChange={closeDialog}
        />
      )}
    </>
  );
}
